'use strict';
const { prepareState, prepareNodes } = require('./utils/preparation');
const operators = require('./utils/operators');
const { Answers } = require('./utils/consts');
const { getIsOtherAnswerWithTextSelected } = require('./utils/helpers');

module.exports = class Executor {
    /**
     * Создает экземпляр Executor.
     * @param {object} state - начальное состояние (иерархия узлов)
     * @param {function} onChange - колбэк, вызываемый при изменении состояния логики
     * @param {function} onError - колбэк, вызываемый в случае ошибок
     */
    constructor({
        state,
        onError = console.error.bind(console),
        onChange = console.info.bind(console)
    }) {
        this._state = prepareState(state);
        this._exprNodes = prepareNodes(state, onError);
        this._onChange = onChange;
        this._onError = onError;
        this._immediate = fn => setTimeout(fn, 1);
        this._recalcStatusCounter = 0;
        this._init();
    }

    /**
     * Задает ответ для вопроса.
     * @param {string} qId - идентификатор вопроса
     * @param {*} val - ответ
     */
    setAnswer(qId, val) {
        // Наивная, но тупая проверка на Array-like
        // Для пустого массива мы должны считать, что ответа нет.
        if (val && typeof val === 'object' && val.length === 0) {
            val = null;
        }
        this._setStateAndRecalc(['questions', qId], val);
    }

    /**
     * Задает значение для опции "Свой вариант" соответствующего вопроса.
     * @param {string} qId - идентификатор вопроса
     * @param {*} val - текст ответа
     */
    setOtherAnswerWithText(qId, val) {
        this._setStateAndRecalc(['questions', qId, 'other'], val);
    }

    /**
     * Обновляет состояние и запускает пересчет скрытых/показываемых узлов.
     * Операция пересчета запускается на следующем тике, либо на одном из ближайших тиков,
     * если среда выполнения не поддерживает setImmediate.
     * @param {array} path - массив строк, из которого конструируется путь к соответствующему узлу
     * @param {*} val - ответ
     */
    _setStateAndRecalc(path, val) {
        this._getNodeState(path)._value = val;
        // Отслеживаем статус пересчета логики
        this._recalcStatusCounter++;
        this._onChange({ status: 'start' });
        this._immediate(() => this._recalcNodes());
    }

    /**
     * Возвращает состояние.
     * @param {array} path - массив строк, из которого конструируется путь к соответствующему узлу
     * @return {object}
     */
    _getNodeState(path) {
        let node = this._state;
        path.forEach(nodeName => {
            if (!(nodeName in node)) {
                node[nodeName] = {};
            }
            node = node[nodeName];
        });

        return node;
    }

    _recalcNodes() {
        try {
            const changed = [];
            let lastLen = 0;
            do {
                lastLen = changed.length;
                changed.push(...this._recalcChangedNodes());
            } while (changed.length !== lastLen);

            if (changed.length) {
                this._onChange({
                    status: 'changed',
                    nodes: changed.map(item => Object.assign({}, item))
                });
            }

            this._recalcStatusCounter--;

            if (this._recalcStatusCounter < 1) {
                this._onChange({ status: 'end' });
            }
        } catch (e) {
            this._onError(e);
        }
    }

    /**
     * Вычисляет узлы, состояние которых поменялось.
     * @return {array} массив узлов, поменявших состояние
     */
    _recalcChangedNodes() {
        return this._exprNodes.filter(node => {
            const val = node.expr();
            if (val !== node.state) {
                node.state = val;
                // Для невидимых вопросов состояние сбрасывается
                if (node.type === 'question' && !node.state) {
                    this._getNodeState(['questions', node.id])._value = null;
                    this._getNodeState(['questions', node.id, 'other'])._value = null;
                }
                return true;
            }
            return false;
        });
    }

    _process(scheme) {
        let type = scheme.type;
        let fn = {
            condition: scheme => this._checkCondition(scheme),
            block: scheme => this._checkBlock(scheme)
        }[type];

        return fn(scheme);
    }

    /**
     * Вернет значение ответа
     * @param {array} node - [<тип_элемента>,<индекс_елемента>]
     * @returns {array} - Всегда возвращает массив. Если значения не проставлено - массив будет пустым
     */
    _getNodeValue(node) {
        const [type, index] = node;
        const value = this._state[type][index] && this._state[type][index]._value;
        const otherValue = this._state[type][index] &&
            this._state[type][index].other &&
            this._state[type][index].other._value;

        const isOtherAnswerWithTextSelected = getIsOtherAnswerWithTextSelected(value);
        const res = [];

        if (isOtherAnswerWithTextSelected && !otherValue) {
            Array.prototype.push.apply(
                res,
                value
                    .filter(answer => String(Answers.OTHER_ANSWER_WITH_TEXT) !== String(answer))
                    .map(answer => answer ? answer : '')
            );
        } else if (Array.isArray(value)) {
            Array.prototype.push.apply(res, value);
        } else if (value) {
            res.push(value);
        }

        return res;
    }

    _checkCondition(scheme) {
        const { node, op, value } = scheme;

        // there could be no value - always true condition
        const currentValue = node === undefined ? undefined : this._getNodeValue(node);

        return operators.condition[op](currentValue, value);
    }

    _checkBlock(scheme) {
        let { op, content } = scheme;

        // Если блок с условиями пустой, то не учитываем его в логике
        if (!content.length) {
            return true;
        }

        // "or" или "and"
        return operators.block[op](content, this._process.bind(this));
    }

    _compile(expr) {
        return () => this._process(expr);
    }

    /**
     * Генерирует код функций проверки видимости узлов и создает соответствующие функции.
     * Задает начальное состояние узлов.
     */
    _init() {
        const hidden = [];

        for (let i = 0; i < this._exprNodes.length; i++) {
            const node = this._exprNodes[i];

            if (typeof node.expr !== 'function') {
                node.expr = this._compile(node.expr);
            }

            node.state = node.expr();

            if (!node.state) {
                hidden.push(node);
            }
        }

        this._onChange({ status: 'start' });

        if (hidden.length) {
            this._onChange({
                status: 'changed',
                nodes: hidden.map(item => Object.assign({}, item))
            });
        }

        this._onChange({ status: 'end' });
    }
};
